<script lang="ts" setup>
import type { CSSProperties } from 'vue';

import type { Addon, AddonGroup, Item, Product } from '~/models';

import AddonGroupItem from './AddonGroupItem.vue';

import { useNuxtApp } from '#app';
import { useAddon } from '~/composables/useAddon';
import { useAddonGroup } from '~/composables/useAddonGroup';

interface State {
  groups: AddonGroup[];
  quantity: number;
}

const props = defineProps<{
  product: Product;
  fullscreen: boolean;
}>();

const emits = defineEmits<{
  (e: 'close'): void;
  (e: 'confirm', item: Item): void;
}>();

const route = useRoute();
const router = useRouter();
const { $http } = useNuxtApp();

const state = reactive<State>({
  groups: [],
  quantity: 1
});

const isLoading = ref<boolean>(true);

const loadAddons = async (): Promise<void> => {
  try {
    state.groups = await $http.product.addons(props.product.uuid);
    useAddonGroup(state.groups).normalize();
  } finally {
    isLoading.value = false;
  }
};

const cardStyle = computed<CSSProperties>(() => ({
  maxHeight: props.fullscreen ? 'none' : '75svh',
  borderRadius: props.fullscreen ? '0' : '0.25rem'
}));

const addButtonLabel = computed<string>(() => {
  const extra = useAddonGroup(state.groups).total();
  const total = (props.product.price + extra) * state.quantity;

  const textTotal = useCurrency().format(Math.round((total + Number.EPSILON) * 100) / 100);

  return `Add ${textTotal}`;
});

const allMandatoriesWereSelected = computed<boolean>(() => {
  return useAddonGroup(state.groups).allMandatoriesWereSelected();
});

const onRemoveAddon = (group: AddonGroup, addon: Addon): void => {
  useAddon(group).remove(addon);
  useAddonGroup(state.groups).updateDependents();
};

const onIncreaseQuantity = (): void => {
  state.quantity++;
};

const onDecreaseQuantity = (): void => {
  state.quantity--;
};

const onConfirmAddons = async (): Promise<void> => {
  const item = await useItem().mount(
    {
      ...props.product,
      addongroups: state.groups
    },
    useAddonGroup(state.groups).cartAddonGroups(),
    state.quantity,
    useAddonGroup(state.groups).total(),
    useAddonGroup(state.groups).drsTotal()
  );

  emits('confirm', item);
  onClose();
};

const onAddAddon = (group: AddonGroup, addon: Addon): void => {
  useAddon(group).add(addon);
  useAddonGroup(state.groups).updateDependents();

  const choices = group.addons
    .map((addon) => addon.quantity)
    .reduce((acc, curr) => (acc + curr), 0);

  if (group.maxChoices === choices) {
    const nextGroup = state.groups
      .slice(state.groups.indexOf(group) + 1)
      .find((group) => group.visible);

    if (!nextGroup) {
      return;
    }

    const index = state.groups.indexOf(nextGroup);
    setTimeout(() => location.href = `#group${index}`);
  }
};

const onClose = (): void => {
  router.replace({ path: route.path });
  emits('close');
};

loadAddons();
</script>

<template>
  <UCard
    class="item-addons flex-1 flex flex-col select-none overflow-hidden"
    :style="cardStyle"
    data-test="card"
    :ui="{
      header: {
        base: 'flex-initial shadow-lg',
        padding: 'sm:px-2 sm:py-0 px-2 py-0'
      },
      body: {
        base: props.fullscreen ? 'flex-1 overflow-y-auto' : 'overflow-y-auto',
        padding: 'sm:p-0 p-0'
      },
      footer: {
        base: 'flex-initial',
        padding: 'sm:p-2 p-2'
      }
    }"
  >
    <template #header>
      <UContainer class="item-addons__header grid items-center text-center">
        <span
          class="item-addons__name uppercase text-base font-roboto-bold"
          data-test="name"
        >
          {{ props.product.name }}
        </span>

        <span
          v-if="props.product.description"
          class="item-addons__description border-t-2 border-company-1"
        >
          <span
            class="leading-8 text-xs subtext-card font-semibold"
            data-test="description"
          >
            {{ props.product.description }}
          </span>
        </span>

        <UButton
          class="text-inherit rounded-full item-addons__close"
          variant="ghost"
          data-test="close"
          aria-label="close"
          @click="onClose()"
        >
          <template #leading>
            <UIcon
              name="ic:round-close"
              class="text-2xl text-inherit"
            />
          </template>
        </UButton>
      </UContainer>
    </template>

    <ul
      class="item-addons__addons"
      :class="{ 'flex-1': props.fullscreen }"
      data-test="list"
    >
      <AddonGroupItem
        v-for="(group, index) of state.groups"
        :id="`group${index}`"
        :key="index"
        :group="group"
        :product-price="product.price"
        @add-addon="onAddAddon"
        @remove-addon="onRemoveAddon"
      />
    </ul>

    <template #footer>
      <div class="item-addons__footer flex flex-col gap-2 items-center">
        <div class="flex items-center justify-around p-2 gap-4 w-1/2 border border-card bg-input rounded-sm text-3xl">
          <UButton
            class="rounded-full text-company-1"
            variant="ghost"
            data-test="decrease"
            aria-label="minus"
            :disabled="state.quantity <= 1"
            @click="onDecreaseQuantity"
          >
            <template #leading>
              <UIcon
                class="text-2xl"
                name="ic:round-minus"
              />
            </template>
          </UButton>

          <span data-test="quantity">
            {{ state.quantity }}
          </span>

          <UButton
            class="rounded-full text-company-1"
            variant="ghost"
            data-test="increase"
            aria-label="add"
            @click="onIncreaseQuantity"
          >
            <template #leading>
              <UIcon
                class="text-2xl"
                name="ic:round-add"
              />
            </template>
          </UButton>
        </div>

        <UButton
          block
          class="bg-company-1 py-3 font-semibold text-xl uppercase"
          :disabled="!allMandatoriesWereSelected"
          :loading="isLoading"
          :label="addButtonLabel"
          data-test="add"
          @click="onConfirmAddons()"
        />
      </div>
    </template>
  </UCard>
</template>

<style lang="scss" scoped>
.item-addons {

  &__header {
    grid-template-areas:
      'space name close'
      'desc desc desc';
    grid-template-columns: 2.25rem 1fr 2.25rem;
    padding: 0.5rem 0 0 0;
    row-gap: 0.5rem;

    &>.item-addons {
      &__name {
        grid-area: name;
      }

      &__description {
        grid-area: desc;
      }

      &__close {
        grid-area: close;
      }
    }
  }

  &__addons {
    overflow: auto;
  }
}
</style>
